/**
 * WhatsNewDialog — a one-time "what's new" popover, keyed on the app version.
 *
 * On mount it compares the running version (__APP_VERSION__) against the last
 * version the user acknowledged (localStorage). If the app has advanced, it
 * shows a tidy catch-up of every release since — then records the current
 * version on dismiss so it won't reappear until the next release. Purely
 * client-side: no server rows, no email, no notification-bell fan-out.
 *
 * Mounted once inside AppLayout, so it only ever runs for a signed-in user.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Sparkle24Filled } from '@fluentui/react-icons'
import { CURRENT_VERSION, pendingReleases, type ReleaseNote } from '../data/whatsNew'

const SEEN_KEY = 'dac.whatsNewSeenVersion'

// Exported for the welcome dialog: a null `seen` is how the layout knows this
// is someone's first run, and the welcome stands in for this popover then.
export function readSeen(): string | null {
  try {
    return window.localStorage.getItem(SEEN_KEY)
  } catch {
    return null // private mode / storage disabled
  }
}

export function writeSeen(version: string): void {
  try {
    window.localStorage.setItem(SEEN_KEY, version)
  } catch {
    // storage unavailable — the popover may re-show next load; harmless.
  }
}

// A dialog the page opened itself — a ticket reached from a notification link
// is one. Stacked on top of it, this popover takes the keyboard: the dialog
// underneath looks ready to type in and every key goes nowhere. Our own
// surface is the one modal that does not count.
function anotherModalOpen(own: HTMLElement | null): boolean {
  return Array.from(document.querySelectorAll('[aria-modal="true"]')).some(
    (el) => !own || (el !== own && !own.contains(el)),
  )
}

const useStyles = makeStyles({
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  icon: { color: '#1f5bb5' },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxHeight: '52vh',
    overflowY: 'auto',
  },
  release: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  ver: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  list: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
})

export function WhatsNewDialog() {
  const styles = useStyles()
  const navigate = useNavigate()
  const [items, setItems] = useState<ReleaseNote[]>([])
  const [open, setOpen] = useState(false)
  // Pending but stepping aside for another dialog. Not a dismissal: nothing
  // is recorded, and the popover comes back once that dialog closes.
  const [waiting, setWaiting] = useState(false)
  const surfaceRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const pending = pendingReleases(readSeen())
    if (pending.length > 0) {
      setItems(pending)
      setOpen(true)
    }
  }, [])

  // Watched rather than checked once: the page's own dialog can mount in the
  // same render as this one (/tickets/:id opens from an effect), after any
  // check made on mount.
  useEffect(() => {
    if (!open) return
    const check = () => setWaiting(anotherModalOpen(surfaceRef.current))
    check()
    const observer = new MutationObserver(check)
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [open])

  function acknowledge() {
    writeSeen(CURRENT_VERSION)
    setOpen(false)
  }

  function seeAll() {
    acknowledge()
    navigate('/whats-new')
  }

  if (!open) return null

  return (
    <Dialog
      open={!waiting}
      onOpenChange={(_, d) => {
        if (!d.open) acknowledge()
      }}
    >
      <DialogSurface ref={surfaceRef} data-testid="whats-new-dialog">
        <DialogBody>
          <DialogTitle className={styles.title}>
            <Sparkle24Filled className={styles.icon} aria-hidden />
            What’s new
          </DialogTitle>
          <DialogContent className={styles.content}>
            {items.map((r) => (
              <div key={r.version} className={styles.release} data-testid={`whats-new-${r.version}`}>
                <span className={styles.ver}>
                  v{r.version} · {r.date}
                </span>
                <ul className={styles.list}>
                  {r.highlights.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </div>
            ))}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={seeAll} data-testid="whats-new-see-all">
              See all updates
            </Button>
            <Button appearance="primary" onClick={acknowledge} data-testid="whats-new-dismiss">
              Got it
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
